/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: SEO Text Block PD component — title + paragraph + collapsible second paragraph + Read More toggle
'use client';
import { useState } from 'react';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { cn } from '@/lib/utils';
import { useInView } from '@/hooks/use-in-view';
import { getAlignClasses, getMarginBottomClass, getTextStyle } from '@/lib/page-designer/style-attributes';

/* v8 ignore start */
@Component('scoText', {
    name: 'SEO Text Block',
    description:
        'SEO copy block with a title, first paragraph always visible, and a collapsible second paragraph revealed by Read More.',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class ScoTextMetadata {
    @AttributeDefinition({ name: 'Title', type: 'string' })
    title?: string;

    @AttributeDefinition({ id: 'paragraph1', name: 'Paragraph 1', type: 'markup' })
    paragraph1?: string;

    @AttributeDefinition({ id: 'paragraph2', name: 'Paragraph 2 (collapsible)', type: 'markup' })
    paragraph2?: string;

    @AttributeDefinition({
        id: 'headingFontStyle',
        name: 'Style – Heading Font',
        type: 'enum',
        values: ['Default', 'Playfair Display', 'Inter'],
        defaultValue: 'Default',
    })
    headingFontStyle?: string;

    @AttributeDefinition({
        id: 'headingFontSize',
        name: 'Style – Heading Size',
        type: 'enum',
        values: ['Default', 'Small', 'Medium', 'Large', 'X-Large'],
        defaultValue: 'Default',
    })
    headingFontSize?: string;

    @AttributeDefinition({
        id: 'headingColor',
        name: 'Style – Heading Color',
        type: 'string',
        description: 'Hex color, e.g. #1A1A1A or #fff. Leave blank for the default.',
    })
    headingColor?: string;

    @AttributeDefinition({
        id: 'bodyFontStyle',
        name: 'Style – Body Font',
        type: 'enum',
        values: ['Default', 'Playfair Display', 'Inter'],
        defaultValue: 'Default',
    })
    bodyFontStyle?: string;

    @AttributeDefinition({
        id: 'bodyFontSize',
        name: 'Style – Body Size',
        type: 'enum',
        values: ['Default', 'Small', 'Medium', 'Large', 'X-Large'],
        defaultValue: 'Default',
    })
    bodyFontSize?: string;

    @AttributeDefinition({
        id: 'bodyColor',
        name: 'Style – Body Color',
        type: 'string',
        description: 'Hex color, e.g. #1A1A1A or #fff. Leave blank for the default.',
    })
    bodyColor?: string;

    @AttributeDefinition({
        id: 'textAlign',
        name: 'Style – Text Alignment',
        type: 'enum',
        values: ['Default', 'Left', 'Center', 'Right'],
        defaultValue: 'Default',
    })
    textAlign?: string;

    @AttributeDefinition({
        id: 'marginBottom',
        name: 'Style – Margin Bottom',
        type: 'enum',
        values: ['0', '2', '4', '6', '8', '12', '16'],
        defaultValue: '0',
        description: 'Space below the component (Tailwind spacing scale; 4 = 16px).',
    })
    marginBottom?: string;
}
/* v8 ignore stop */

export default function ScoText({
    title,
    paragraph1,
    paragraph2,
    headingFontStyle,
    headingFontSize,
    headingColor,
    bodyFontStyle,
    bodyFontSize,
    bodyColor,
    textAlign,
    marginBottom,
}: {
    title?: string;
    paragraph1?: string;
    paragraph2?: string;
    headingFontStyle?: string;
    headingFontSize?: string;
    headingColor?: string;
    bodyFontStyle?: string;
    bodyFontSize?: string;
    bodyColor?: string;
    textAlign?: string;
    marginBottom?: string;
}) {
    const [expanded, setExpanded] = useState(false);
    const { ref, inView } = useInView<HTMLElement>({ margin: '-80px' });
    const heading = getTextStyle(
        { fontStyle: headingFontStyle, fontSize: headingFontSize, color: headingColor },
        'heading'
    );
    const bodyText = getTextStyle({ fontStyle: bodyFontStyle, fontSize: bodyFontSize, color: bodyColor }, 'body');
    // Default keeps the block's original centered copy.
    const align = getAlignClasses(textAlign) ?? getAlignClasses('Center');

    return (
        <section
            ref={ref}
            className={cn(
                'bg-background py-14 lg:py-20 border-t border-border df-reveal',
                inView && 'df-reveal--show',
                getMarginBottomClass(marginBottom)
            )}>
            <div className="max-w-[880px] mx-auto px-6 lg:px-8">
                {title && (
                    <h2
                        style={{ fontFamily: 'var(--font-display)', ...heading.style }}
                        className={cn(
                            'text-3xl lg:text-4xl font-light text-foreground mb-6',
                            align?.text,
                            heading.className
                        )}>
                        {title}
                    </h2>
                )}

                {paragraph1 && (
                    <div
                        style={bodyText.style}
                        className={cn(
                            'prose prose-sm max-w-none text-muted-foreground leading-relaxed',
                            align?.text,
                            bodyText.className
                        )}
                        // oxlint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: paragraph1 }}
                    />
                )}

                {paragraph2 && (
                    <>
                        <div
                            className={cn(
                                'prose prose-sm max-w-none text-muted-foreground leading-relaxed mt-4 overflow-hidden transition-all duration-300',
                                align?.text,
                                bodyText.className,
                                expanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                            )}
                            style={bodyText.style}
                            // oxlint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: paragraph2 }}
                        />
                        <div className={cn('flex mt-6', align?.justify)}>
                            <button
                                type="button"
                                onClick={() => setExpanded((v) => !v)}
                                className="text-xs tracking-[0.16em] uppercase font-medium underline underline-offset-2 text-foreground hover:opacity-60 transition-opacity">
                                {expanded ? 'Read Less' : 'Read More'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
